import { useEffect, useId, useRef, useState } from 'react';
import { taxPaymentSettingsGateway } from '../index';
import type { StateTaxComponentDefinition, StateTaxSetup } from '../types';
import { formatDate, statusTone, type TaxListRow } from '../taxPaymentView';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { AuditHistoryModal } from './AuditHistoryModal';
import { EmployerDetailsPanel } from './EmployerDetailsPanel';
import { NeedsReviewPanel, StateTaxTabPanel } from './StateTaxTabPanel';

interface JurisdictionDrawerProps {
  row: TaxListRow;
  /** False when the caller may only read: entries cannot be added or removed. */
  canManage: boolean;
  /** Abbreviation of the tax component whose chip opened the drawer (for example "SDI"); the drawer opens on the tab that holds it. */
  initialComponent?: string | null;
  /** Last four digits of the company EIN, or null when none is on file. */
  einLast4: string | null;
  /** Called after an entry or a review changed the setup, so the caller can refresh what it derives from it (the row's status). */
  onSetupChanged?: () => void;
  onClose: () => void;
}

const ACCOUNTS_TAB = 'accounts';

/** The label of a component's chip in the list: its own `chipLabel` when it has one, otherwise its abbreviation. */
const chipLabelOf = (c: StateTaxComponentDefinition | undefined) => c?.chipLabel ?? c?.abbreviation;

/**
 * The right-side detail drawer. It is schema-driven: the tabs and their sections come from the state's setup schema, so a state with a
 * different set of tax components needs a schema, not a new screen. A state without a schema (and Federal) shows only the employer account
 * details. It opens over the list without leaving the route, so the list's search, filter and scroll position are untouched.
 */
export function JurisdictionDrawer({ row, canManage, einLast4, initialComponent = null, onSetupChanged, onClose }: JurisdictionDrawerProps) {
  const titleId = useId();
  const closeRef = useRef<HTMLButtonElement>(null);
  const [setup, setSetup] = useState<StateTaxSetup | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [tab, setTab] = useState<string | null>(null);
  const [auditOpen, setAuditOpen] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const isFederal = row.jurisdictionKey === 'federal';

  useEffect(() => {
    if (isFederal) return undefined;
    const controller = new AbortController();
    taxPaymentSettingsGateway
      .getStateTax(row.jurisdictionKey, controller.signal)
      .then(setSetup)
      .catch((err) => { if (!controller.signal.aborted) setLoadError(friendlyErrorMessage(err, 'Could not load the tax setup.')); });
    return () => controller.abort();
  }, [row.jurisdictionKey, isFederal]);

  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !auditOpen) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, auditOpen]);

  const markReviewed = async () => {
    setReviewing(true);
    setReviewError(null);
    try {
      setSetup(await taxPaymentSettingsGateway.reviewStateTax(row.jurisdictionKey));
      onSetupChanged?.();
    } catch (err) {
      setReviewError(friendlyErrorMessage(err, 'Could not record the review.'));
    } finally {
      setReviewing(false);
    }
  };

  const schema = setup?.schema ?? null;
  const review = setup?.review ?? null;
  const reviewCurrent = review !== null && !review.changedSince;
  // A tab is flagged while one of its components carries an advisory and has no value in force yet.
  const needsAttention = (componentKeys: string[]) => componentKeys.some((k) =>
    schema?.components.find((c) => c.key === k)?.missingAdvisory && !setup?.components.find((c) => c.componentKey === k)?.entries.some((e) => e.isCurrent));
  const tabs = [
    ...(schema?.tabs.map((t) => ({ key: t.key, label: t.label, attention: needsAttention(t.componentKeys) })) ?? []),
    { key: ACCOUNTS_TAB, label: 'Employer Accounts', attention: false },
  ];
  const chipTab = initialComponent && schema
    ? schema.tabs.find((t) => t.componentKeys.some((k) => chipLabelOf(schema.components.find((c) => c.key === k)) === initialComponent))?.key
    : undefined;
  const activeTab = tab ?? chipTab ?? tabs[0]?.key ?? ACCOUNTS_TAB;
  const tone = statusTone(row.status);

  return (
    <div className="tps-scrim" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <aside className="tps-drawer" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <button ref={closeRef} type="button" className="tps-close" aria-label="Close tax setup details" onClick={onClose}>
          <span className="tps-ico tps-ico--x" aria-hidden="true" />
        </button>

        <div className="tps-drawer__scroll">
        <div className="tps-drawer__head">
          <h3 id={titleId} className="tps-drawer__title">{row.jurisdiction}</h3>
          <p className="tps-drawer__sub">{row.workLocationName}{row.address ? ` · ${row.address}` : ''}</p>
        </div>

        <h4 className="tps-drawer__overview">Overview</h4>
        <div className="tps-info" role="note">
          <span className="tps-ico tps-ico--info" aria-hidden="true" />
          <span>This tax setup was created based on the company's confirmed branch/work location.</span>
        </div>
        <dl className="tps-facts">
          <div><dt>EIN</dt><dd>{einLast4 ? `**-***${einLast4}` : 'Not on file'}</dd></div>
          <div><dt>Branch Address</dt><dd>{row.address ?? 'Not available'}</dd></div>
          <div><dt>Work Location</dt><dd>{row.workLocationName}</dd></div>
          <div><dt>No. of Employees</dt><dd>{row.employees}</dd></div>
          <div>
            <dt>Last Verified</dt>
            <dd>
              {reviewCurrent ? formatDate(review.reviewedAtUtc) : 'Not verified yet'}
              {review && (
                <span className="tps-muted tps-small tps-review-note">
                  {review.changedSince
                    ? `Values changed since the review on ${formatDate(review.reviewedAtUtc)}.`
                    : `Reviewed by ${review.reviewedBy}. An employer review: nothing was checked against state agency rules.`}
                </span>
              )}
              {canManage && schema && !schema.reviewStatus && !reviewCurrent && (
                <button type="button" className="tps-link" onClick={() => void markReviewed()} disabled={reviewing}>
                  {reviewing ? 'Saving…' : 'Mark as reviewed'}
                </button>
              )}
              {reviewError && <span className="tps-error" role="alert">{reviewError}</span>}
            </dd>
          </div>
          <div><dt>Setup Status</dt><dd><span className={`tps-pill tps-pill--${tone}`}>{row.status}</span></dd></div>
        </dl>

        {schema?.reviewStatus && (
          <div className="tps-draft-note" role="note">
            <span><strong>{schema.reviewStatus}.</strong> {schema.reviewNote}</span>
          </div>
        )}

        {loadError && <p className="tps-error" role="alert">{loadError}</p>}
        {!isFederal && !setup && !loadError && <p role="status">Loading…</p>}

        {(isFederal || setup) && (
          <>
            <div className="tps-tabs" role="tablist" aria-label={`${row.jurisdiction} tax setup`}>
              {tabs.map((t) => (
                <button
                  key={t.key} type="button" role="tab" id={`tps-tab-${t.key}`} aria-selected={activeTab === t.key} aria-controls={`tps-panel-${t.key}`}
                  className={`tps-tab${activeTab === t.key ? ' tps-tab--active' : ''}`} onClick={() => setTab(t.key)}
                >
                  {t.label}
                  {t.attention && (<><span className="tps-tab-dot" aria-hidden="true" /><span className="tps-sr-only"> needs attention</span></>)}
                </button>
              ))}
            </div>

            <div className="tps-drawer__body">
              {schema && setup && tabs.filter((t) => t.key !== ACCOUNTS_TAB).map((t) =>
                activeTab === t.key ? (
                  <div key={t.key} role="tabpanel" id={`tps-panel-${t.key}`} aria-labelledby={`tps-tab-${t.key}`}>
                    {(() => {
                      const def = schema.tabs.find((x) => x.key === t.key);
                      return def?.reviewStatus
                        ? <NeedsReviewPanel name={def.label} status={def.reviewStatus} description={def.description} />
                        : (
                          <StateTaxTabPanel
                            jurisdictionKey={row.jurisdictionKey} setup={setup} readOnly={!canManage}
                            componentKeys={def?.componentKeys ?? []} onChanged={(next) => { setSetup(next); onSetupChanged?.(); }}
                          />
                        );
                    })()}
                    <p className="tps-muted tps-disclosure">{schema.disclosure}</p>
                  </div>
                ) : null)}

              {activeTab === ACCOUNTS_TAB && (
                <div role="tabpanel" id={`tps-panel-${ACCOUNTS_TAB}`} aria-labelledby={`tps-tab-${ACCOUNTS_TAB}`}>
                  {!isFederal && !schema && (
                    <p className="tps-muted">Rates and deposit schedules for {row.jurisdiction} are not set up in EPN yet. You can record the employer account details below.</p>
                  )}
                  <EmployerDetailsPanel key={row.jurisdictionKey} jurisdictionKey={row.jurisdictionKey} jurisdiction={row.jurisdiction} />
                </div>
              )}
            </div>
          </>
        )}

        </div>

        <div className="tps-drawer__footer">
          <button type="button" className="tps-link" onClick={onClose}>Close</button>
          {schema && <button type="button" className="tps-btn tps-btn--green" onClick={() => setAuditOpen(true)}>View Audit History</button>}
        </div>
      </aside>

      {auditOpen && (
        <AuditHistoryModal
          jurisdictionKey={row.jurisdictionKey} jurisdiction={row.jurisdiction} subtitle={`${row.workLocationName} · `} onClose={() => setAuditOpen(false)}
        />
      )}
    </div>
  );
}
